import {PromptTarget} from '../state/useNovaPromptState';
import {NovaPromptInput, NovaVariable, buildIsTokenPermitted} from './novaVariableLibrary';
import {NovaMarkdownWarning, scanMarkdown} from './novaMarkdownScan';

export const NovaIssueKind = {
	// The reference names a variable that existed and has since been removed.
	BrokenReference: 'BROKEN_REFERENCE',
	// The reference names something that was never a variable.
	UnknownVariable: 'UNKNOWN_VARIABLE',
	// The variable exists but may not be used in this input.
	NotPermitted: 'NOT_PERMITTED',
	// The Markdown around the references is structurally broken.
	Markdown: 'MARKDOWN',
} as const;
export type NovaIssueKind = (typeof NovaIssueKind)[keyof typeof NovaIssueKind];

export interface NovaInputIssue {
	kind: NovaIssueKind;
	// The reference the issue is about, braces included. Absent on a Markdown warning.
	token?: string;
	// Which Markdown rule fired. Absent on a reference issue.
	warning?: NovaMarkdownWarning;
	// Where the variable *is* allowed, so the reason can name it. Only on a not-permitted issue.
	permittedInputs?: NovaPromptInput[];
}

export interface NovaValidationContext {
	// The variables the library offers for insertion.
	offered: NovaVariable[];
	// The variables that have been removed, served separately so a reference to one reads as broken
	// rather than as a name that never existed.
	removed: NovaVariable[];
}

const FILE_TOKEN_PREFIX = '{file:';

// Classifies one reference against the library, or returns null when there is nothing to say about
// it. Order matters: a removed variable reads as broken even in the input it used to be allowed in,
// because "this no longer exists" is more actionable than "this is not allowed here".
//
// `isTokenPermitted` defaults to a fresh predicate built from `context.offered` so a caller that
// only ever validates one token can still call this with three arguments. `validateInput` builds
// the predicate once and passes it down instead, since it classifies every distinct token in an
// input against the same offered list. Either way this is the same permission rule the variable
// library panel and `insertTokenAtCursor` enforce — see `buildIsTokenPermitted` — so a clause added
// there is picked up here automatically instead of having to be restated.
export function classifyToken(
	token: string,
	target: PromptTarget,
	context: NovaValidationContext,
	isTokenPermitted: (token: string, target: PromptTarget) => boolean = buildIsTokenPermitted(context.offered)
): NovaInputIssue | null {
	if (context.removed.some(variable => variable.token === token)) {
		return {kind: NovaIssueKind.BrokenReference, token};
	}
	const offered = context.offered.find(variable => variable.token === token);
	if (!offered) {
		// A file reference the library no longer offers points at a detached attachment — something
		// that did exist, so broken rather than unknown.
		return token.startsWith(FILE_TOKEN_PREFIX)
			? {kind: NovaIssueKind.BrokenReference, token}
			: {kind: NovaIssueKind.UnknownVariable, token};
	}
	if (!isTokenPermitted(token, target)) {
		return {kind: NovaIssueKind.NotPermitted, token, permittedInputs: offered.permittedInputs};
	}
	return null;
}

// Everything worth telling the author about one authored input: bad references first, then Markdown
// warnings. Every issue is advisory — none of them blocks saving, and none of them changes the text.
// A blank input is silent, which is what makes an input with no content a valid, complete one.
//
// A reference is reported once however often it appears, so an input that mentions the same removed
// variable five times gets one line about it rather than five.
export function validateInput(text: string, target: PromptTarget, context: NovaValidationContext): NovaInputIssue[] {
	if (text.trim() === '') {
		return [];
	}
	const scan = scanMarkdown(text);
	// Built once per input rather than once per distinct reference — every occurrence in this text
	// is classified against the same offered list, so there is no reason to rebuild it per token.
	const isTokenPermitted = buildIsTokenPermitted(context.offered);
	const issues: NovaInputIssue[] = [];
	const reported = new Set<string>();
	for (const occurrence of scan.tokens) {
		if (reported.has(occurrence.token)) {
			continue;
		}
		reported.add(occurrence.token);
		const issue = classifyToken(occurrence.token, target, context, isTokenPermitted);
		if (issue) {
			issues.push(issue);
		}
	}
	for (const warning of scan.warnings) {
		issues.push({kind: NovaIssueKind.Markdown, warning});
	}
	return issues;
}
